import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { DateFormats } from "@common/ux/date-formats";
import { PeopleService } from "app/features/people/people.service";
import { forkJoin } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { CoachService } from "../coach.service";
import { CoachUiService } from "../coach-ui.service";

@Component({
    selector: "adapt-coach-relationships",
    templateUrl: "./coach-relationships.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class CoachRelationshipsComponent extends BaseComponent implements OnInit {

    @Input() public organisation?: Organisation;

    public connections: Connection[] = [];
    public dateFormat = DateFormats.globalize.short;

    constructor(
        private coachService: CoachService,
        private coachUiService: CoachUiService,
        private peopleService: PeopleService,
        rxJsBreezeService: RxjsBreezeService,
    ) {
        super();

        rxJsBreezeService.entityTypeChangedInSave(Connection).pipe(
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateData());
    }

    public ngOnInit() {
        this.peopleService.getPeopleForOrganisationId(this.organisation!.organisationId)
            .pipe(
                switchMap(() => this.updateData()),
                this.takeUntilDestroyed(),
            ).subscribe();
    }

    @Autobind
    public addNewCoach() {
        if (!this.organisation) {
            throw new Error("Org should be defined");
        }

        return this.coachUiService.addNewCoachConnection(this.organisation).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        );
    }

    public calculateActiveCellValue(data: Connection) {
        return data.isActive();
    }

    public calculateRolesCellValue(data: Connection) {
        // the sort and reverse are in-place, so the list will be sorted in rolesCellTemplate
        return data.roleConnections
            .sort(SortUtilities.getSortByFieldFunction<RoleConnection>("role.label"))
            .reverse()
            .filter((rc) => rc.isActive() && rc.role && rc.role.roleType && rc.role.extensions.isCoachAccessRole())
            .map((rc) => rc.role.label)
            .join(",");
    }

    public editPractitionerConnection(connection: Connection) {
        return this.coachUiService.editCoachConnection(connection).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public endCoachConnection(connection: Connection) {
        return this.coachUiService.endCoachConnection(connection).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public deletePractitionerConnection(connection: Connection) {
        return this.coachUiService.deleteCoachConnection(connection).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    private updateData() {
        return forkJoin([
            this.coachService.getAllCoachConnectionsForOrganisation(this.organisation!.organisationId),
            // fetch so the coach roles are primed
            this.coachService.getAllCoachRolesForOrganisation(this.organisation!.organisationId),
        ]).pipe(
            tap(([connections]) => this.connections = connections),
        );
    }
}
